import { TextInput } from "@mantine/core"
import { useQuery } from "@tanstack/react-query"
import { useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useAuth } from "@/hooks"
import type { TPersonalType } from "@/types"
import { userAPI } from "@/utils"
import { shirtSizes } from "@/utils/constants"
import { QuestionCity } from "./QuestionCity"
import { QuestionDate } from "./QuestionDate"
import { QuestionSelect } from "./QuestionSelect"
import { QuestionString } from "./QuestionString"
import { QuestionUser } from "./QuestionUser"
import type { TQuestionProps } from "./types"

export function QuestionPersonal(props: TQuestionProps) {
  usePersonal(props)

  const { question } = props
  const category = question.meta.category as TPersonalType

  if (category == "city") return <QuestionCity {...props} />
  if (category == "manager") return <QuestionUser {...props} />
  if (category == "birthday") return <QuestionDate {...props} />
  if (category == "start_date") return <QuestionDate {...props} />
  if (category == "legal_name") return <QuestionString {...props} maxRows={1} />
  if (category == "position") return <QuestionString {...props} maxRows={1} />
  if (category == "size") return <QuestionSelect {...props} choices={shirtSizes} />
  if (category == "quote") return <QuestionString {...props} />
  if (category == "intro") return <QuestionString {...props} />
  if (category == "phone") return <QuestionPhone {...props} />
  if (category == "email") return <QuestionEmail {...props} />

  return <QuestionString {...props} />
}

const personalCity = (user) => (user?.city_id ? parseInt(user.city_id, 10) : null)
const personalManager = (user) => (user?.manager_id ? parseInt(user.manager_id, 10) : null)
const personalBirthday = (user) => (user?.birthday ? new Date(user.birthday).valueOf() / 1000 : null)
const personalStartDate = (user) => (user?.dtstart ? new Date(user.dtstart).valueOf() / 1000 : null)

function usePersonal({ value, comment, setValue, setComment, question }: TQuestionProps) {
  const { user } = useAuth()

  const category = question.meta?.category as TPersonalType
  const { data: { user_info } = {} } = useQuery({
    enabled:
      !!user &&
      (category == "legal_name" ||
        category == "size" ||
        category == "quote" ||
        category == "intro" ||
        category == "phone"),
    queryKey: ["details", user?.id],
    queryFn: () => userAPI.details(user!.id),
  })

  useEffect(() => {
    if (value || comment) return

    if (!user) return

    if (category == "city") setValue(personalCity(user))
    else if (category == "manager") setValue(personalManager(user))
    else if (category == "birthday") setValue(personalBirthday(user))
    else if (category == "start_date") setValue(personalStartDate(user))
    else if (category == "position") setComment(user.position)
    else if (category == "email") setComment(user.email || "")
    // User Info
    else if (category == "legal_name") setComment(user_info?.legal_name || "")
    else if (category == "size") setValue(user_info?.size ? shirtSizes.indexOf(user_info.size) : null)
    else if (category == "quote") setComment(user_info?.quote || "")
    else if (category == "intro") setComment(user_info?.intro || "")
    else if (category == "phone") setComment(user_info?.phone || "")
  }, [category, user, user_info])
}

function QuestionPhone({ field, comment, setComment, autoFocus, question, preview }: TQuestionProps) {
  const { t } = useTranslation()
  const { onBlur, onFocus, error } = field || {}
  const isInvalid = comment && /[a-zA-Z]/.test(comment)

  return (
    <TextInput
      size="lg"
      type="tel"
      error={error || (isInvalid ? t("Invalid phone") : undefined)}
      onBlur={onBlur}
      onFocus={onFocus}
      disabled={preview}
      autoFocus={autoFocus}
      value={comment ?? ""}
      required={question.is_required}
      onChange={(e) => setComment(e.currentTarget.value.length > 0 ? e.currentTarget.value : null)}
    />
  )
}

function QuestionEmail({ field, comment, setComment, autoFocus, question, preview }: TQuestionProps) {
  const { t } = useTranslation()
  const { onBlur, onFocus, error } = field || {}
  const isInvalid = comment && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(comment)

  return (
    <TextInput
      size="lg"
      type="email"
      error={error || (isInvalid ? t("Invalid email") : undefined)}
      onBlur={onBlur}
      onFocus={onFocus}
      disabled={preview}
      autoFocus={autoFocus}
      value={comment ?? ""}
      required={question.is_required}
      onChange={(e) => setComment(e.currentTarget.value.length > 0 ? e.currentTarget.value : null)}
    />
  )
}
